
import { Vue, Component, Prop, Inject } from 'vue-property-decorator';
import RotatingBacklight
  from '@/ui/components/devices/components/EnergyParts/RotatingBacklight.vue';
import ShowEventBaseGroup from '@/ui/components/devices/mpc/base/ShowEventBase.vue';
import BatteryLevel from '@/ui/components/devices/components/EnergyParts/BatteryLevel.vue';
import {
  EnergyCircleDisplayData,
  CirclePosition,
  PlaceholderEnergyCircleDisplayData,
} from '@/types/energyVisualisation/EnergyCircleDisplayData';
import EnergyCircleValue
  from '@/ui/components/devices/components/EnergyParts/EnergyVisualisation/EnergyCircle/EnergyCircleValue.vue';
import ChargeIndicator
  from '@/ui/components/devices/components/EnergyParts/ChargeIndicator/index.vue';
import HouseCalculationInfo
  from '@/ui/components/devices/components/EnergyParts/HouseCalculationInfo/index.vue';
import {
  EnergyCircleType,
  isConsumer,
  isProducer
} from '@/types/energyVisualisation/EnergyCircleType';
import { kiloWattUnit } from '@/utils/unit/unitConstants';
import { convertNegativeZero, countDecimalPlaces } from '@/utils/utilsFunctions';
import EmsModeIndicator from '@/ui/components/devices/components/EnergyParts/EmsModeIndicator.vue';
import AdditionalIndicator
  from '@/ui/components/devices/components/EnergyParts/AdditionalIndicator.vue';

@Component({
  components: {
    AdditionalIndicator,
    EmsModeIndicator,
    BatteryLevel,
    RotatingBacklight,
    ShowEventBaseGroup,
    EnergyCircleValue,
    ForceChargeIndicator: ChargeIndicator,
    HouseCalculationInfo,
  },
  methods: {convertNegativeZero},
})
export default class EnergyCircle extends Vue {
  @Prop({}) displayData!: EnergyCircleDisplayData;
  @Prop({default: 'ActualView'}) view!: 'ActualView' | 'EnergyFlowView';
  @Inject({
    from: 'onCircleClick', default: () => {
    }
  }) onCircleClick?: (
    circleData: EnergyCircleDisplayData,
  ) => void;

  /**
   * Returns the bottom value formatted with one decimal place if the value has more than one decimal place.
   */
  get formattedBottomValue() {
    let val: any = this.displayData.bottomValue;
    if (typeof this.displayData.bottomValue === 'number' && countDecimalPlaces(this.displayData.bottomValue) > 1) {
      val = parseFloat(this.displayData.bottomValue.toFixed(1));
    }
    const id = this.displayData.id.replace(/[0-9]/g, '') as EnergyCircleType;
    if (val < 0 && (isProducer(id) || isConsumer(id))) {
      return val * -1;
    }
    return val;
  }

  get energyCircleValueValue() {
    return this.unit.unitForValue(convertNegativeZero(this.formattedBottomValue ?? 0)).formattedWithUnit;
  }

  get energyCircleValueShow() {
    return this.displayData.hideValue !== false && !this.showLoadingAnimationHouseCircle && this.displayData.bottomValue !== undefined;
  }

  get titlePosition() {
    return this.displayData.position === CirclePosition.bottom ? 'bottom' : 'top';
  }

  get systemCountPosition() {
    return this.displayData.position === CirclePosition.bottom
      ? 'system-count-bottom'
      : 'system-count-top';
  }

  get isPlaceholder() {
    return this.displayData instanceof PlaceholderEnergyCircleDisplayData;
  }

  get isBatterySoc() {
    return this.displayData.centerContent.type === 'soc';
  }

  get isForceChargeIndicator() {
    return this.isBatterySoc &&
      (
        this.displayData.additionalDisplayData?.forceCharge ||
        this.displayData.additionalDisplayData?.calibrationCharge ||
        this.displayData.additionalDisplayData?.maintenanceCharge ||
        this.displayData.additionalDisplayData?.chargeMaintenance
      );
  }

  get isGrid() {
    return this.displayData.id === 'grid';
  }

  get p14aActive() {
    return this.displayData.additionalDisplayData?.p14aActive;
  }

  get feedInLimitationActive() {
    return this.displayData.additionalDisplayData?.feedInLimitationActive;
  }

  get p14aActiveInfo() {
    return {
      icon: 'mdi-script-text',
      documentation: {
        title: this.$t('mlModel.EMSV2.mainView.p14aActive'),
        info: this.$t('mlModel.EMSV2.mainView.p14aActiveInfo'),
      },
    };
  }

  get feedInLimitationActiveInfo() {
    return {
      icon: 'mdi-gauge-low',
      documentation: {
        title: this.$t('mlModel.EMSV2.mainView.feedInLimitationActive'),
        info: this.$t('mlModel.EMSV2.mainView.feedInLimitationActiveInfo'),
      },
    };
  }

  get isEmsModeIndicator() {
    return (this.isGrid && this.view === 'ActualView') && !!this.displayData.additionalDisplayData?.activeEmsMode;
  }

  get showLoadingAnimationHouseCircle() {
    return this.displayData.additionalDisplayData?.showLoadingAnimationHouseCircle ?? false;
  }

  get unit() {
    return this.displayData.unit ?? kiloWattUnit;
  }

  get rotatingBacklightNoData() {
    if (this.displayData.additionalDisplayData?.forceAnimation) return false;
    return !(this.displayData.bottomValue ?? this.displayData.alternativeValue);
  }

  get disableSpinner() {
    return this.displayData.disableSpinner !== true;
  }

  get valuePositionClass() {
    return this.displayData.position === CirclePosition.bottom
      ? 'energy-circle-bottom-value'
      : 'energy-circle-top-value';
  }

  get circleContentStyle() {
    if (this.displayData.bottomValue === undefined) {
      return {};
    } else {
      return {
        transform:
          this.displayData.position === CirclePosition.bottom
            ? 'translateY(4px)'
            : 'translateY(-4px)',
      };
    }
  }

  energyCircleOnClick() {
    this.onCircleClick?.(this.displayData);
  }
}
